import { useEffect, useState } from 'react';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core-react/i18n';

import { useMediaQuery } from '#src/app/shared/hooks/dom';

// The pauses are deliberately fixed rather than per character, so a long phrase doesn't also sit
// still for longer.
const TYPE_MS_PER_CHAR = 72;
const ERASE_MS_PER_CHAR = 36;
const HOLD_MS = 1400;
const GAP_MS = 400;

/** Each phase schedules the next, a character at a time while typing and erasing. */
type Phase = 'typing' | 'holding' | 'erasing' | 'gap';

type Result = ModelHookResult<{
	hasPhrase: boolean;
	/** The whole phrase under reduced motion, otherwise the slice typed so far. */
	text: string;
	phase: Phase;
}>;

/**
 * Cycles through `phrases`: each is typed a character at a time, held, erased, then followed by a
 * beat on the empty field. Under `prefers-reduced-motion` nothing is scheduled and the first phrase
 * is returned whole.
 */
export function useTypewriterPlaceholder(phrases: readonly TranslationKey[]): Result {
	const { typedTranslate } = useI18n();
	const [index, setIndex] = useState(0);
	const [phase, setPhase] = useState<Phase>('typing');
	const [revealed, setRevealed] = useState(0);
	const motionReduced = useMediaQuery('(prefers-reduced-motion: reduce)');

	const activeKey = phrases.length > 0 ? phrases[index % phrases.length] : undefined;
	const phrase = activeKey ? typedTranslate(activeKey) : '';

	useEffect(() => {
		if (!phrase || motionReduced) return undefined;

		if (phase === 'holding') {
			const timer = setTimeout(() => setPhase('erasing'), HOLD_MS);

			return () => clearTimeout(timer);
		}

		if (phase === 'gap') {
			const timer = setTimeout(() => {
				setIndex((current) => (phrases.length > 0 ? (current + 1) % phrases.length : 0));
				setPhase('typing');
			}, GAP_MS);

			return () => clearTimeout(timer);
		}

		// The phase hands off from inside the timer so no render is triggered synchronously by an
		// effect (react-hooks/set-state-in-effect).
		const typing = phase === 'typing';
		const next = typing ? Math.min(revealed + 1, phrase.length) : Math.max(revealed - 1, 0);
		const timer = setTimeout(
			() => {
				setRevealed(next);
				if (typing && next >= phrase.length) setPhase('holding');
				if (!typing && next <= 0) setPhase('gap');
			},
			typing ? TYPE_MS_PER_CHAR : ERASE_MS_PER_CHAR,
		);

		return () => clearTimeout(timer);
	}, [phase, revealed, phrase, motionReduced, phrases.length]);

	return {
		data: {
			hasPhrase: !!phrase,
			text: motionReduced ? phrase : phrase.slice(0, revealed),
			phase,
		},
	};
}
